// AlertsPage.jsx — /alertas — DINO worldwide alerts, asset-page layout
// Left: big gradient alert card (RRAlertSingleCard style) + queue nav
// Right: news feed (AssetNewsPanel style)
// Bottom: compact list of remaining alerts in selected region

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import Silk from '../components/Silk';

const SHOW_API    = '/api/show/api/show';
const VOLUME_API  = '/api/show/api/show/volume';

const EXCHANGE_FLAG = {
  SA:'br', T:'jp', KS:'kr', L:'gb', DE:'de', PA:'fr', AS:'nl', MI:'it',
  ST:'se', OL:'no', SW:'ch', BR:'be', VI:'at', HE:'fi', IR:'ie', AX:'au',
  NZ:'nz', SI:'sg', JK:'id', KL:'my', NS:'in', HK:'hk', TW:'tw', SS:'cn',
  SR:'sa', AE:'ae', KW:'kw', QA:'qa', TA:'il', TO:'ca', MX:'mx', SN:'cl',
};

function getFlagUrl(ticker) {
  if (!ticker) return null;
  if (!ticker.includes('.')) return 'https://flagcdn.com/w20/us.png';
  const suf = ticker.split('.').pop().toUpperCase();
  const cc = EXCHANGE_FLAG[suf];
  return cc ? `https://flagcdn.com/w20/${cc}.png` : null;
}

function fmtPrice(price, currency) {
  if (!price || price <= 0) return null;
  if (!currency || currency === 'BRL') return `R$ ${price.toFixed(2)}`;
  const noDecimals = ['JPY','KRW','IDR','CLP','TWD'].includes(currency);
  return `${currency} ${noDecimals ? Math.round(price) : price.toFixed(2)}`;
}

function fmtVolBRL(v) {
  if (!v) return null;
  if (v >= 1e9) return `R$${(v/1e9).toFixed(1)}B`;
  if (v >= 1e6) return `R$${(v/1e6).toFixed(1)}M`;
  return `R$${(v/1e3).toFixed(0)}K`;
}

function getRegion(ticker) {
  if (!ticker) return 'b3';
  if (/^[A-Z0-9]{4}3[2-5]$/.test(ticker)) return 'b3';
  if (!ticker.includes('.')) return 'americas';
  const suf = ticker.split('.').pop().toUpperCase();
  if (suf === 'SA') return 'b3';
  if (['TO','MX','SN'].includes(suf)) return 'americas';
  if (['SR','AE','KW','QA','TA'].includes(suf)) return 'gulf';
  if (['L','DE','PA','AS','ST','OL','SW','BR','VI','HE','IR','MI','AT'].includes(suf)) return 'europe';
  if (['T','KS','TW','AX','NZ','SI','JK','KL','NS','HK','SS','SZ'].includes(suf)) return 'asia';
  return 'other';
}

const REGIONS = [
  { key: 'all',         label: '🌐 Todos'          },
  { key: 'b3',          label: '🇧🇷 B3'             },
  { key: 'americas',    label: '🌎 Américas'        },
  { key: 'asia',        label: '🌏 Ásia/Pacífico'   },
  { key: 'europe',      label: '🌍 Europa'          },
  { key: 'gulf',        label: '🟡 Golfo & MENA'    },
];

const TYPES = [
  { key: 'all',     label: 'Todos'    },
  { key: 'extreme', label: '⚡ Extremos' },
  { key: 'up',      label: '▲ Altas'  },
  { key: 'down',    label: '▼ Baixas' },
  { key: 'volume',  label: '📊 Volume' },
];

const getSentimentEmoji = (s) => s === 'positive' ? '📈' : s === 'negative' ? '📉' : '📰';

// ── Hero alert panel (left col) ──────────────────────────────
function HeroAlert({ alert, queue, onNext, onPrev, history }) {
  const navigate = useNavigate();
  if (!alert) return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '3rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
      <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.15em' }}>SEM ALERTAS ATIVOS</div>
    </div>
  );

  const isGain = alert.direction === 'up' || alert.type === 'gain';
  const bgGradient = isGain
    ? 'linear-gradient(135deg, rgba(16,185,129,0.95), rgba(5,150,105,0.98))'
    : 'linear-gradient(135deg, rgba(239,68,68,0.95), rgba(220,38,38,0.98))';
  const price = fmtPrice(alert.price_brl > 0 ? alert.price_brl : alert.price, alert.price_brl > 0 ? 'BRL' : alert.currency);

  return (
    <div style={{ background: bgGradient, borderRadius: 16, padding: '24px', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)' }}>
      {/* top row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, fontWeight: 700, color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
          {isGain ? '🚀' : '📉'} ALERTA
        </div>
        {alert.isExtreme && (
          <div style={{ background: 'rgba(255,255,255,0.25)', padding: '4px 12px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 800, color: '#fff', border: '1px solid rgba(255,255,255,0.3)', letterSpacing: '0.1em' }}>⚡ EXTREMO</div>
        )}
      </div>

      {/* ticker */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          {getFlagUrl(alert.ticker) && (
            <img src={getFlagUrl(alert.ticker)} alt="" style={{ width: 24, height: 16, borderRadius: 3, objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
          )}
          <div style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: '2.8rem', fontWeight: 800, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.3)', lineHeight: 1 }}>
            {alert.ticker}
          </div>
        </div>
        <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: '1rem', color: 'rgba(255,255,255,0.9)', marginBottom: 10 }}>{alert.name}</div>
        {price && <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: '1.4rem', fontWeight: 600, color: '#fff', marginBottom: 6 }}>{price}</div>}
        <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: '2.2rem', fontWeight: 700, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
          {isGain ? '▲' : '▼'} {Math.abs(alert.change).toFixed(2)}%
        </div>
        {alert.financial_vol_brl > 0 && (
          <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 6 }}>
            Vol. {fmtVolBRL(alert.financial_vol_brl)}
          </div>
        )}
      </div>

      {/* queue indicator */}
      {queue.length > 0 && (
        <div style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.15)', borderRadius: 6, fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', textAlign: 'center', marginBottom: 12, border: '1px solid rgba(255,255,255,0.2)' }}>
          +{queue.length} na fila
        </div>
      )}

      {/* buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <button disabled={!history.length} onClick={onPrev} style={{ padding: '10px 5px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, color: '#fff', fontSize: '0.75rem', fontWeight: 800, cursor: history.length ? 'pointer' : 'not-allowed', opacity: history.length ? 1 : 0.4 }}>◀ ANTERIOR</button>
        <button onClick={() => navigate(`/asset/stock/${alert.ticker}`)} style={{ padding: '10px 5px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 8, color: isGain ? '#059669' : '#dc2626', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer' }}>📊 VER</button>
        <button disabled={!queue.length} onClick={onNext} style={{ padding: '10px 5px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, color: '#fff', fontSize: '0.75rem', fontWeight: 800, cursor: queue.length ? 'pointer' : 'not-allowed', opacity: queue.length ? 1 : 0.4 }}>
          {queue.length > 0 ? '⏭️ PRÓXIMO' : '✕ FIM'}
        </button>
      </div>
    </div>
  );
}

// ── News panel (right col, asset-page style) ─────────────────
function NewsPanel({ news, loading }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', marginBottom: 14 }}>📰 NOTÍCIAS RECENTES</div>

      {loading && !news.length && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.25)', fontFamily: "'IBM Plex Mono',monospace", fontSize: 10 }}>Carregando notícias…</div>
      )}
      {!loading && !news.length && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.2)', fontFamily: "'IBM Plex Mono',monospace", fontSize: 10 }}>Nenhuma notícia recente</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {news.map((item, i) => (
          <div key={i} onClick={() => window.open(item.url, '_blank')}
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
          >
            <div style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: '0.95rem', fontWeight: 800, color: '#fff', lineHeight: 1.35, marginBottom: 8 }}>{item.title}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em' }}>{item.source}</span>
              {item.sentiment && (
                <><span style={{ color: 'rgba(255,255,255,0.2)' }}>·</span>
                <span style={{ fontSize: 11 }}>{getSentimentEmoji(item.sentiment)}</span></>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Compact alert row (below the hero) ───────────────────────
function CompactAlertRow({ alert, onClick, isActive }) {
  const isGain = alert.direction === 'up' || alert.type === 'gain';
  const chgColor = alert.severity === 'extreme' ? '#f43f5e' : isGain ? '#4ade80' : '#f87171';
  const price = fmtPrice(alert.price_brl > 0 ? alert.price_brl : alert.price, alert.price_brl > 0 ? 'BRL' : alert.currency);

  return (
    <div onClick={onClick}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 8, cursor: 'pointer', background: isActive ? 'rgba(102,96,254,0.12)' : 'rgba(255,255,255,0.03)', border: `1px solid ${isActive ? 'rgba(102,96,254,0.4)' : 'rgba(255,255,255,0.06)'}`, transition: 'all 0.15s' }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {getFlagUrl(alert.ticker) && (
          <img src={getFlagUrl(alert.ticker)} alt="" style={{ width: 16, height: 11, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
        )}
        <div>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, fontWeight: 800, color: '#D4AF37' }}>{alert.ticker}</span>
          {alert.severity === 'extreme' && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 7, color: '#f43f5e', marginLeft: 5 }}>⚡</span>}
          <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{alert.name}</div>
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 13, fontWeight: 700, color: chgColor }}>{isGain ? '▲' : '▼'} {Math.abs(alert.change).toFixed(2)}%</div>
        {price && <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>{price}</div>}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function AlertsPage() {
  const [searchParams] = useSearchParams();
  const [allAlerts, setAllAlerts]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [regionFilter, setRegion]   = useState(searchParams.get('region') || 'all');
  const [typeFilter, setType]       = useState('all');
  const [currentAlert, setCurrent]  = useState(null);
  const [queue, setQueue]           = useState([]);
  const [history, setHistory]       = useState([]);
  const [news, setNews]             = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);

  const theme = {
    background: 'rgba(8,6,15,0.95)', textPrimary: '#fff', textSecondary: '#999',
    border: 'rgba(102,96,254,0.2)', borderHover: 'rgba(102,96,254,0.5)',
    cardBg: 'rgba(13,11,24,0.9)', tickerBg: 'rgba(0,0,0,0.4)',
  };

  // fetch data
  useEffect(() => {
    const load = async () => {
      try {
        const [s, v] = await Promise.all([fetch(SHOW_API), fetch(VOLUME_API)]);
        const show = await s.json();
        const vol  = await v.json();
        const all = [
          ...(show.alerts || []),
          ...(vol.volume_movers || show.volume_movers || []),
        ].map(a => ({ ...a, type: a.direction === 'up' ? 'gain' : 'loss', isExtreme: a.severity === 'extreme' }));
        setAllAlerts(all);
        setLoading(false);
      } catch { setLoading(false); }
    };
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  // filter alerts
  const filtered = allAlerts.filter(a => {
    const r = getRegion(a.ticker);
    const regionOk = regionFilter === 'all' || r === regionFilter;
    const typeOk = typeFilter === 'all'
      || (typeFilter === 'extreme' && a.severity === 'extreme')
      || (typeFilter === 'up'      && a.direction === 'up')
      || (typeFilter === 'down'    && a.direction === 'down')
      || (typeFilter === 'volume'  && a.severity === 'volume');
    return regionOk && typeOk;
  });

  // seed hero when filters change
  useEffect(() => {
    if (!filtered.length) { setCurrent(null); setQueue([]); setHistory([]); return; }
    setCurrent(filtered[0]);
    setQueue(filtered.slice(1));
    setHistory([]);
    setNews([]);
  }, [regionFilter, typeFilter, allAlerts.length]);

  // fetch news for current alert
  useEffect(() => {
    if (!currentAlert) return;
    setNews([]);
    if (currentAlert.news?.length) { setNews(currentAlert.news.slice(0, 5)); return; }
    setNewsLoading(true);
    fetch(`/api/news82/api/news/ticker/${currentAlert.ticker}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setNews(d.news?.slice(0, 5) || []); setNewsLoading(false); })
      .catch(() => setNewsLoading(false));
  }, [currentAlert]);

  const handleNext = () => {
    if (!queue.length) return;
    setHistory(p => [...p, currentAlert]);
    setCurrent(queue[0]);
    setQueue(p => p.slice(1));
    setNews([]);
  };

  const handlePrev = () => {
    if (!history.length) return;
    setQueue(p => [currentAlert, ...p]);
    setCurrent(history[history.length - 1]);
    setHistory(p => p.slice(0, -1));
    setNews([]);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#06040f', position: 'relative' }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.25 }}>
        <Silk speed={25} scale={1.5} color="#6660fe" noiseIntensity={1.5} rotation={0.1} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header theme={theme} isDark={true} toggleTheme={() => {}} topTickers={[]} />

        <main style={{ maxWidth: 1400, margin: '0 auto', padding: '2rem 2rem 4rem' }}>

          {/* Page header */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
              <h1 style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: '2.6rem', fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>Alertas de Mercado</h1>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, color: '#6660fe', border: '1px solid #6660fe44', padding: '2px 8px', borderRadius: 3, letterSpacing: '0.15em' }}>DINO WORLDWIDE</span>
            </div>
            <p style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, color: 'rgba(255,255,255,0.3)', fontSize: 13, margin: 0 }}>
              {allAlerts.length} alertas · {allAlerts.filter(a => a.severity === 'extreme').length} extremos
            </p>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: '1.5rem', alignItems: 'center' }}>
            {REGIONS.map(r => (
              <button key={r.key} onClick={() => setRegion(r.key)} style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, padding: '5px 12px', borderRadius: 4, cursor: 'pointer', letterSpacing: '0.08em', background: regionFilter === r.key ? '#6660fe' : 'rgba(255,255,255,0.04)', border: `1px solid ${regionFilter === r.key ? '#6660fe' : 'rgba(255,255,255,0.1)'}`, color: regionFilter === r.key ? '#fff' : 'rgba(255,255,255,0.5)', transition: 'all 0.15s' }}>
                {r.label}
              </button>
            ))}
            <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
            {TYPES.map(t => (
              <button key={t.key} onClick={() => setType(t.key)} style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, padding: '5px 12px', borderRadius: 4, cursor: 'pointer', letterSpacing: '0.08em', background: typeFilter === t.key ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.04)', border: `1px solid ${typeFilter === t.key ? '#D4AF37' : 'rgba(255,255,255,0.1)'}`, color: typeFilter === t.key ? '#D4AF37' : 'rgba(255,255,255,0.5)', transition: 'all 0.15s' }}>
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '6rem', fontFamily: "'IBM Plex Mono',monospace", color: '#6660fe', letterSpacing: '0.15em' }}>CARREGANDO ALERTAS…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>

              {/* LEFT col: hero + compact list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <HeroAlert alert={currentAlert} queue={queue} onNext={handleNext} onPrev={handlePrev} history={history} />

                {/* compact list of the rest */}
                {filtered.filter(a => a !== currentAlert).length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.15em', marginBottom: 4 }}>
                      OUTROS ALERTAS · {filtered.length - 1}
                    </div>
                    {filtered.filter(a => a !== currentAlert).map((a, i) => (
                      <CompactAlertRow
                        key={`${a.ticker}-${i}`}
                        alert={a}
                        isActive={false}
                        onClick={() => {
                          setHistory(p => currentAlert ? [...p, currentAlert] : p);
                          setCurrent(a);
                          setNews([]);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* RIGHT col: news */}
              <div style={{ position: 'sticky', top: 100 }}>
                <NewsPanel news={news} loading={newsLoading} />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}